import { useEffect, useState } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  PaymentsOutlined as StripeIcon,
  CheckCircleOutlineOutlined as CheckIcon,
  ErrorOutlineOutlined as ErrorIcon,
  VisibilityOutlined as ShowIcon,
  VisibilityOffOutlined as HideIcon,
  SaveOutlined as SaveIcon,
  ScienceOutlined as TestIcon,
  WarningAmberOutlined as WarningIcon,
  RocketLaunchOutlined as LaunchIcon,
  OpenInNewOutlined as ExternalIcon,
  ContentCopyOutlined as CopyIcon,
} from '@mui/icons-material';
import {
  getStripeSettings,
  listSubscriptionPlansAdmin,
  startTestCheckout,
  testStripeConnection,
  updateStripeSettings,
} from '../../services/stripeApi';
import { toServerDate } from '../../timezone';

const formatTimestamp = (ts) => {
  if (!ts) return null;
  try {
    return toServerDate(ts)?.toLocaleString() ?? ts;
  } catch {
    return ts;
  }
};

/**
 * Operator-facing Stripe configuration page.
 *
 * <p>The secret material (secret key, webhook secret) is never echoed
 * back from the server — we display "(set)" / "(not set)" indicators
 * and let the operator type a fresh value to rotate. Empty submit
 * leaves the field unchanged; explicit "Clear" button on each secret
 * sends an empty string to wipe it.
 */
const OperatorStripeSettingsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showHook, setShowHook] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [successUrl, setSuccessUrl] = useState('');
  const [cancelUrl, setCancelUrl] = useState('');
  const [mode, setMode] = useState('test');

  // Test-signup panel state
  const [allPlans, setAllPlans] = useState([]);
  const [testPlanCode, setTestPlanCode] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testCheckout, setTestCheckout] = useState(null);
  const [testError, setTestError] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const s = await getStripeSettings();
      setSettings(s);
      setPublishableKey(s.publishableKey || '');
      setSignupEnabled(!!s.signupEnabled);
      setSuccessUrl(s.successUrl || '');
      setCancelUrl(s.cancelUrl || '');
      setMode(s.mode || 'test');
      // Don't pre-populate secret/webhook fields — leave them blank
      // so accidental save doesn't clear them.
      setSecretKey('');
      setWebhookSecret('');
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not load Stripe settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    listSubscriptionPlansAdmin()
      .then((r) => setAllPlans(Array.isArray(r) ? r : (r?.content || [])))
      .catch(() => setAllPlans([]));
  }, []);

  const testablePlans = (allPlans || []).filter(
    (p) => p.stripePriceId && p.stripePriceId.trim() !== ''
  );

  const handleSave = async () => {
    setSaving(true);
    setSaved(null);
    setError(null);
    try {
      const payload = {
        publishableKey,
        signupEnabled,
        successUrl,
        cancelUrl,
        mode,
      };
      if (secretKey.trim()) payload.secretKey = secretKey.trim();
      if (webhookSecret.trim()) payload.webhookSecret = webhookSecret.trim();
      const updated = await updateStripeSettings(payload);
      setSettings(updated);
      setSecretKey('');
      setWebhookSecret('');
      setSaved('Stripe settings saved.');
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.lastTestStatus || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClearSecret = async (which) => {
    if (!window.confirm(`Clear the ${which === 'secret' ? 'secret API key' : 'webhook secret'}?`)) return;
    setSaving(true);
    try {
      const payload = which === 'secret' ? { secretKey: '' } : { webhookSecret: '' };
      const updated = await updateStripeSettings(payload);
      setSettings(updated);
      setSaved(`${which === 'secret' ? 'Secret key' : 'Webhook secret'} cleared.`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Clear failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testStripeConnection();
      setTestResult(r);
      // Refresh settings to pick up lastTestAt / lastTestStatus.
      reload();
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.message || e.message });
    } finally {
      setTesting(false);
    }
  };

  const handleTestCheckout = async () => {
    setTestRunning(true);
    setTestCheckout(null);
    setTestError(null);
    try {
      const r = await startTestCheckout(testPlanCode, testEmail);
      if (!r?.ok) {
        setTestError(r?.message || 'Could not create test checkout');
      } else {
        setTestCheckout(r);
      }
    } catch (e) {
      setTestError(e?.response?.data?.message || e.message || 'Test checkout failed');
    } finally {
      setTestRunning(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* clipboard rejected — silent. */
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <TableSkeleton rows={5} columns={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <StripeIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h4" fontWeight={700}>Stripe integration</Typography>
        {settings?.mode === 'live' ? (
          <Chip label="LIVE MODE" color="error" size="small" sx={{ fontWeight: 700 }} />
        ) : (
          <Chip label="TEST MODE" color="info" size="small" sx={{ fontWeight: 700 }} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure the platform's Stripe account. Keys are stored encrypted at rest.
        The publishable key ships to the browser at signup time; the secret key never leaves the server.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(null)}>{saved}</Alert>}
      {testResult && (
        <Alert
          severity={testResult.ok ? 'success' : 'error'}
          sx={{ mb: 2 }}
          icon={testResult.ok ? <CheckIcon /> : <ErrorIcon />}
          onClose={() => setTestResult(null)}
        >
          {testResult.ok
            ? `Connected to Stripe${testResult.accountName ? ` — ${testResult.accountName}` : ''}${testResult.accountCountry ? ` (${testResult.accountCountry})` : ''}.`
            : `Test failed: ${testResult.message}`}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>API keys</Typography>

        <Stack spacing={2}>
          <Box>
            <TextField
              label="Secret key"
              type={showSecret ? 'text' : 'password'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={settings?.secretKeySet ? '•••••••••• (set, leave blank to keep)' : 'sk_test_… or sk_live_…'}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowSecret((v) => !v)}>
                      {showSecret ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={
                <Stack direction="row" spacing={1} alignItems="center" component="span">
                  <span>Status:</span>
                  {settings?.secretKeySet ? (
                    <Chip icon={<CheckIcon sx={{ fontSize: 12 }} />} label="set" size="small" color="success" sx={{ height: 18 }} />
                  ) : (
                    <Chip icon={<WarningIcon sx={{ fontSize: 12 }} />} label="not set" size="small" color="warning" sx={{ height: 18 }} />
                  )}
                  {settings?.secretKeySet && (
                    <Button size="small" sx={{ ml: 'auto' }} onClick={() => handleClearSecret('secret')}>
                      Clear
                    </Button>
                  )}
                </Stack>
              }
            />
          </Box>

          <TextField
            label="Publishable key"
            value={publishableKey}
            onChange={(e) => setPublishableKey(e.target.value)}
            placeholder="pk_test_… or pk_live_…"
            fullWidth
            helperText="Sent to the browser. Safe to expose."
          />

          <Box>
            <TextField
              label="Webhook signing secret"
              type={showHook ? 'text' : 'password'}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={settings?.webhookSecretSet ? '•••••••••• (set, leave blank to keep)' : 'whsec_…'}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowHook((v) => !v)}>
                      {showHook ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={
                <Stack direction="row" spacing={1} alignItems="center" component="span">
                  <span>Status:</span>
                  {settings?.webhookSecretSet ? (
                    <Chip icon={<CheckIcon sx={{ fontSize: 12 }} />} label="set" size="small" color="success" sx={{ height: 18 }} />
                  ) : (
                    <Chip icon={<WarningIcon sx={{ fontSize: 12 }} />} label="not set" size="small" color="warning" sx={{ height: 18 }} />
                  )}
                  <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
                    Required to verify Stripe webhook signatures.
                  </Box>
                </Stack>
              }
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2}>
          <TextField
            label="Mode"
            select
            SelectProps={{ native: true }}
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            sx={{ maxWidth: 200 }}
          >
            <option value="test">Test</option>
            <option value="live">Live</option>
          </TextField>

          <TextField
            label="Checkout success URL"
            value={successUrl}
            onChange={(e) => setSuccessUrl(e.target.value)}
            placeholder="https://verops.io/signup/success?session_id={CHECKOUT_SESSION_ID}"
            fullWidth
            helperText="Where Stripe redirects after a successful payment."
          />

          <TextField
            label="Checkout cancel URL"
            value={cancelUrl}
            onChange={(e) => setCancelUrl(e.target.value)}
            placeholder="https://verops.io/signup"
            fullWidth
            helperText="Where Stripe redirects if the user cancels."
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={signupEnabled}
                onChange={(e) => setSignupEnabled(e.target.checked)}
                color="success"
                disabled={!settings?.secretKeySet || !publishableKey}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Enable public signup
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Master flag — when off, /api/v1.0/signup returns 403 and the marketing site hides the Sign up button.
                </Typography>
              </Box>
            }
          />
          {(!settings?.secretKeySet || !publishableKey) && (
            <Tooltip title="Set both keys before enabling signup">
              <WarningIcon sx={{ color: 'warning.main' }} />
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save settings
          </Button>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={14} /> : <TestIcon />}
            onClick={handleTest}
            disabled={testing || !settings?.secretKeySet}
          >
            Test connection
          </Button>
          {settings?.lastTestAt && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Last test: {formatTimestamp(settings.lastTestAt)} — {settings.lastTestStatus}
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* ── Test signup flow ─────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <LaunchIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700}>Test signup flow</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Run a real Stripe Checkout for any plan that has a Price ID — without creating
          an organisation or user, and regardless of whether public signup is enabled.
          Use this to verify your keys, price IDs, redirect URLs, and webhook delivery
          are wired correctly before flipping the master switch on.
        </Typography>

        {testablePlans.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No plans have a Stripe Price ID set yet. Add one on the{' '}
            <Link href="/operator/subscription-plans" underline="hover">Subscription Plans</Link>{' '}
            page first (paste from the Stripe dashboard), then come back.
          </Alert>
        )}

        {!settings?.secretKeySet && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Set the secret API key above before running the test.
          </Alert>
        )}

        <Stack spacing={2} sx={{ maxWidth: 720 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ flex: 1 }} disabled={testablePlans.length === 0}>
              <InputLabel>Plan</InputLabel>
              <Select
                label="Plan"
                value={testPlanCode}
                onChange={(e) => setTestPlanCode(e.target.value)}
              >
                <MenuItem value="" disabled>
                  <em>Choose a plan to test</em>
                </MenuItem>
                {testablePlans.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{p.name}</span>
                      {p.displayPrice && (
                        <Chip label={p.displayPrice} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {p.signupEnabled && (
                        <Chip label="signup" size="small" color="success" sx={{ height: 18, fontSize: '0.62rem' }} />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Test email"
              size="small"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="qa+test@verops.io"
              sx={{ flex: 1 }}
              helperText="Stripe sends the receipt here"
            />
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={testRunning ? <CircularProgress size={14} /> : <LaunchIcon />}
              onClick={handleTestCheckout}
              disabled={
                testRunning ||
                !testPlanCode ||
                !settings?.secretKeySet ||
                testablePlans.length === 0
              }
            >
              {testRunning ? 'Creating session…' : 'Create test checkout'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              No org or user is created — this is purely a Stripe-side smoke test.
            </Typography>
          </Stack>

          {testError && <Alert severity="error" onClose={() => setTestError(null)}>{testError}</Alert>}

          {testCheckout?.ok && testCheckout.checkoutUrl && (
            <Alert
              severity="success"
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
              onClose={() => setTestCheckout(null)}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
                Test session created for "{testCheckout.planName}"
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {testCheckout.testCardHint}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<ExternalIcon />}
                  href={testCheckout.checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Stripe Checkout
                </Button>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(testCheckout.checkoutUrl)}
                >
                  Copy URL
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                After completing payment, Stripe redirects to your <strong>success URL</strong>;
                the webhook fires <code>checkout.session.completed</code> tagged
                <code> verops_origin=operator-test</code> so it skips org-activation logic.
              </Typography>
            </Alert>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>How signup works</Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ol style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Operator sets API keys + webhook secret here.</li>
            <li>Operator marks the desired plans as "Available for signup" on the Subscription Plans page,
              and pastes each plan's Stripe Price ID.</li>
            <li>Operator flips the master switch above.</li>
            <li>Marketing site shows a "Sign up" button next to "Login" and renders <code>/signup</code>.</li>
            <li>New tenants land on a free baseline immediately. Stripe Checkout upgrades them to their chosen plan.</li>
          </ol>
        </Typography>
      </Paper>
    </Box>
  );
};

export default OperatorStripeSettingsPage;
